import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, Check, Image as ImageIcon, FileText, HelpCircle } from "lucide-react";
import { PublicShell } from "@/components/site/PublicShell";
import { AppShell } from "@/components/app/AppShell";
import { useAuth } from "@/context/AuthContext";
import { publicApi } from "@/lib/api";

export default function CreatorPreview() {
  const { slug } = useParams();
  const { user } = useAuth();
  const isGuest = !user;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setData(null);
    setError("");
    publicApi
      .promptPreview(slug)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError("Nie udało się wczytać podglądu kreatora."));
    return () => {
      alive = false;
    };
  }, [slug]);

  const Shell = isGuest ? PublicShell : AppShell;
  const fullPath = `/prompt/${slug}`;

  const content = (
    <div data-testid="creator-preview-root" className="mx-auto max-w-3xl">
      <Link
        to="/asystent"
        data-testid="creator-preview-back"
        className="inline-flex items-center gap-2 text-sm font-medium text-brand-sec transition-colors hover:text-brand-text"
      >
        <ArrowLeft size={16} /> Wróć do Asystenta
      </Link>

      {!data && !error && (
        <div className="mt-8 flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-line border-t-brand-accent" />
        </div>
      )}

      {error && (
        <div data-testid="creator-preview-error" className="mt-8 rounded-panel border border-red-500/40 bg-red-500/10 p-6 text-sm text-red-300">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="mt-5 rounded-panel border border-brand-line bg-brand-surface p-6 sm:p-8">
            <div className="flex flex-wrap items-center gap-2 text-xs text-brand-muted">
              <span className="rounded-full bg-brand-elevated px-2.5 py-1">{data.package_name}</span>
              <span className="inline-flex items-center gap-1 rounded-full bg-brand-elevated px-2.5 py-1">
                {data.requires_image ? <ImageIcon size={12} /> : <FileText size={12} />}
                {data.effect_label}
              </span>
              <span className="rounded-full border border-brand-line px-2.5 py-1">Podgląd — tylko do odczytu</span>
            </div>

            <h1 data-testid="creator-preview-name" className="mt-4 font-display text-2xl font-semibold text-brand-text sm:text-3xl">
              {data.name}
            </h1>
            {data.short_benefit && (
              <p className="mt-3 text-brand-sec">{data.short_benefit}</p>
            )}

            {data.questions?.length > 0 && (
              <div data-testid="creator-preview-questions" className="mt-6">
                <p className="text-[13px] font-semibold uppercase tracking-wide text-brand-muted">O co zapytamy</p>
                <ul className="mt-3 space-y-2">
                  {data.questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-brand-text">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-brand-line text-[11px] text-brand-accent">
                        {i + 1}
                      </span>
                      <span>
                        {q.label}
                        {!q.required && <span className="ml-1 text-brand-muted">(opcjonalnie)</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Bramka: rejestracja dopiero przed pełnym formularzem / zapisem / użyciem kredytu */}
          <div
            data-testid="creator-preview-gate"
            className="mt-5 rounded-panel border border-brand-accent/40 bg-brand-accent/[0.06] p-6 sm:p-8"
          >
            <div className="flex items-center gap-2 text-brand-accent">
              <Lock size={18} strokeWidth={2} />
              <span className="font-display text-base font-semibold">
                {data.gate_message || "Załóż bezpłatne konto, aby przygotować i zapisać swój prompt."}
              </span>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-brand-sec">
              <li className="flex items-center gap-2"><Check size={15} className="text-brand-accent" /> Wypełnisz pełny formularz i dostaniesz gotowy prompt</li>
              <li className="flex items-center gap-2"><Check size={15} className="text-brand-accent" /> Zapiszesz swoją pracę i historię</li>
              <li className="flex items-center gap-2"><Check size={15} className="text-brand-accent" /> 10 kredytów na start po potwierdzeniu e-maila</li>
            </ul>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {isGuest ? (
                <>
                  <Link
                    to={`/rejestracja?returnTo=${encodeURIComponent(fullPath)}`}
                    data-testid="creator-preview-register-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                  >
                    Załóż bezpłatne konto <ArrowRight size={16} />
                  </Link>
                  <Link
                    to={`/logowanie?returnTo=${encodeURIComponent(fullPath)}`}
                    data-testid="creator-preview-login-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-6 py-3 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                  >
                    Zaloguj się
                  </Link>
                </>
              ) : (
                <Link
                  to={fullPath}
                  data-testid="creator-preview-open-full"
                  className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-6 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                >
                  Otwórz pełny kreator <ArrowRight size={16} />
                </Link>
              )}
            </div>

            <p className="mt-4 flex items-start gap-2 text-[12.5px] text-brand-muted">
              <HelpCircle size={14} className="mt-0.5 shrink-0" />
              To podgląd tylko do odczytu. Nie zakładasz konta, nie pobieramy kredytów i nie zapisujemy Twojej pracy, dopóki nie przejdziesz do pełnego kreatora.
            </p>
          </div>
        </>
      )}
    </div>
  );

  return <Shell>{content}</Shell>;
}
